import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Download, CheckCircle, Clock, XCircle, User, CreditCard, FileText } from "react-feather";
import RestService from "../../../services/restService";
import Button from "../../../components/common/Button";
import Loader from "../../../components/common/Loader";
import jsPDF from "jspdf";

const TransactionReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const receiptRef = useRef();

  const [transaction, setTransaction] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    RestService.TransactionsApi.getSpecificTransactionDetails(id)
      .then(({ data }) => {
        setTransaction(data.data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching transaction:", error);
        setIsLoading(false);
      });
  }, [id]);

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4'); // Portrait orientation for better financial document layout
      
      // Professional color scheme for financial documents
      const primaryNavy = [25, 47, 89]; // Professional navy blue
      const accentGold = [218, 165, 32]; // Gold accent for premium feel
      const lightGray = [128, 128, 128]; // Neutral gray
      const darkGray = [51, 51, 51]; // Dark gray for text
      const successGreen = [40, 167, 69]; // Success green
      const warningOrange = [255, 133, 27]; // Warning orange
      const backgroundColor = [248, 249, 250]; // Light background
      
      // Page dimensions
      const pageWidth = 210;
      const pageHeight = 297;
      
      // Simple header section
      pdf.setFillColor(...primaryNavy);
      pdf.rect(0, 0, pageWidth, 30, 'F');
      
      // Company branding
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INSTAANT', 20, 15);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('TRANSACTION RECEIPT', 20, 25);
      
      // Receipt info in header
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const receiptNo = `Receipt: ${transaction?.reference || 'N/A'}`;
      const receiptWidth = pdf.getTextWidth(receiptNo);
      pdf.text(receiptNo, pageWidth - 20 - receiptWidth, 15);
      
      const dateStr = new Date(transaction?.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      const dateWidth = pdf.getTextWidth(dateStr);
      pdf.text(dateStr, pageWidth - 20 - dateWidth, 25);
      
      // Reset text color
      pdf.setTextColor(...darkGray);
      
      let yPos = 40;
      
      // Compact Transaction Summary
      const formatCurrency = (amount, currency) => {
        const num = parseFloat(amount || 0);
        return `${currency} ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };
      
      // Summary in a compact box
      pdf.setFillColor(...accentGold);
      pdf.rect(15, yPos, pageWidth - 30, 25, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SEND:', 20, yPos + 8);
      pdf.text('FEE:', 20, yPos + 16);
      pdf.text('RECIPIENT RECEIVES:', 110, yPos + 12);
      
      pdf.setFont('helvetica', 'normal');
      const sendAmount = formatCurrency(transaction?.send_amount, transaction?.source_currency);
      pdf.text(sendAmount, 45, yPos + 8);
      const feeAmount = formatCurrency(transaction?.charge, transaction?.source_currency);
      pdf.text(feeAmount, 45, yPos + 16);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      const receiveAmount = formatCurrency(transaction?.receive_amount, transaction?.destination_currency);
      const receiveWidth = pdf.getTextWidth(receiveAmount);
      pdf.text(receiveAmount, pageWidth - 20 - receiveWidth, yPos + 12);
      
      yPos += 35;
      
      // Compact two-column layout
      const leftColX = 20;
      const rightColX = 110;
      const colWidth = 85;
      
      // Helper function to add compact sections
      const addCompactSection = (title, data, x, startY) => {
        let y = startY;
        
        // Section title
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...primaryNavy);
        pdf.text(title, x, y);
        y += 6;
        
        // Section data
        pdf.setFontSize(8);
        pdf.setTextColor(...darkGray);
        
        Object.entries(data).forEach(([key, value]) => {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...lightGray);
          pdf.text(`${key}:`, x, y);
          
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...darkGray);
          const textLines = pdf.splitTextToSize(value || 'N/A', colWidth - 25);
          pdf.text(textLines, x + 25, y);
          y += 5;
        });
        
        return y + 3;
      };
      
      // Left column sections
      let leftY = yPos;
      
      // Sender Information
      leftY = addCompactSection('SENDER INFORMATION', {
        'Name': `${transaction?.sender?.firstname || ''} ${transaction?.sender?.lastname || ''}`.trim(),
        'Phone': transaction?.sender?.msisdn || 'N/A',
        'Address': transaction?.sender?.address || 'N/A'
      }, leftColX, leftY);
      
      // Recipient Information
      leftY = addCompactSection('RECIPIENT INFORMATION', {
        'Name': `${transaction?.receiver?.firstname || ''} ${transaction?.receiver?.lastname || ''}`.trim(),
        'Phone': transaction?.receiver?.contact_number || 'N/A',
        'Address': transaction?.receiver?.address || 'N/A'
      }, leftColX, leftY);
      
      // Payment Method
      leftY = addCompactSection('PAYMENT METHOD', {
        'Institution': transaction?.receiver_wallet?.destination_institution?.name || 'N/A',
        'Account': transaction?.receiver_wallet?.account_number || 'N/A',
        'Type': transaction?.receiver_wallet?.destination_institution?.type || 'N/A'
      }, leftColX, leftY);
      
      // Right column sections
      let rightY = yPos;
      
      // Transaction Details
      rightY = addCompactSection('TRANSACTION DETAILS', {
        'Reference': transaction?.reference || 'N/A',
        'Partner Ref': transaction?.partner_reference || 'N/A',
        'Purpose': transaction?.transaction_purpose?.purpose || 'N/A',
        'Method': transaction?.transaction_type?.type || 'N/A',
        'Partner': transaction?.partner?.name || 'N/A'
      }, rightColX, rightY);
      
      // Exchange Rate
      rightY = addCompactSection('EXCHANGE RATE', {
        'Rate': `1 ${transaction?.source_currency} = ${parseFloat(transaction?.rate || 0).toFixed(4)} ${transaction?.destination_currency}`
      }, rightColX, rightY);
      
      // Agent Information
      rightY = addCompactSection('AGENT INFORMATION', {
        'Agent': `${transaction?.agent?.firstname || ''} ${transaction?.agent?.lastname || ''}`.trim(),
        'Agency': transaction?.agent?.agency?.name || 'N/A',
        'Contact': transaction?.agent?.agency?.msisdn || 'N/A'
      }, rightColX, rightY);
      
      let currentY = Math.max(leftY, rightY);
      
      // Compact footer section
      let footerY = currentY + 15;
      // Status
      const status = transaction?.transaction_status?.status || 'Completed';
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...successGreen);
      const statusWidth = pdf.getTextWidth(`Status: ${status.toUpperCase()}`);
      pdf.text(`Status: ${status.toUpperCase()}`, pageWidth - 20 - statusWidth, footerY);
      
      // Simple footer
      footerY += 10;
      pdf.setFillColor(...primaryNavy);
      pdf.rect(0, footerY, pageWidth, 20, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INSTAANT MONEY TRANSFER SERVICES', 20, footerY + 8);
      
      pdf.setFont('helvetica', 'normal');
      pdf.text('Licensed Money Transfer Operator', 20, footerY + 15);
      
      const generatedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const footerRightText = `Generated: ${generatedDate}`;
      const footerRightWidth = pdf.getTextWidth(footerRightText);
      pdf.text(footerRightText, pageWidth - 20 - footerRightWidth, footerY + 15);
      
      // Save the PDF
      pdf.save(`receipt-${transaction?.reference || id}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'accepted':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
      case 'dispatched':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed':
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'accepted':
        return <CheckCircle size={16} className="flex-shrink-0" />;
      case 'pending':
      case 'dispatched':
        return <Clock size={16} className="flex-shrink-0" />;
      case 'failed':
      case 'error':
        return <XCircle size={16} className="flex-shrink-0" />;
      default:
        return <Clock size={16} className="flex-shrink-0" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader message="Loading transaction details..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-8"> {/* Added top padding to avoid navbar overlap */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="h-10 w-10 p-0 rounded-full bg-white text-blue-600 border border-gray-200 hover:bg-gray-50 shadow-sm flex-shrink-0"
              aria-label="Back"
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="flex-shrink-0"
              >
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12,19 5,12 12,5"></polyline>
              </svg>
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Transaction Receipt</h1>
              <p className="text-sm sm:text-base text-gray-600">Reference: {transaction?.reference}</p>
            </div>
          </div>
          
          <Button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            loading={isDownloading}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm flex-shrink-0"
            size="sm"
          >
            <Download size={16} className="mr-2" />
            {isDownloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>

        {/* Receipt Content */}
        <div ref={receiptRef} className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Professional Header Section */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-6 text-white relative overflow-hidden">
            {/* Background pattern for professional look */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
            </div>
            
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                      <span className="text-slate-900 font-bold text-lg">I</span>
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold tracking-wide">INSTAANT</h1>
                      <p className="text-slate-300 text-sm font-medium">MONEY TRANSFER SERVICES</p>
                    </div>
                  </div>
                  <h2 className="text-xl font-semibold text-yellow-400 mb-1">OFFICIAL TRANSACTION RECEIPT</h2>
                  <p className="text-slate-300 text-sm">
                    Receipt #: {transaction?.reference}
                  </p>
                  <p className="text-slate-300 text-sm">
                    {new Date(transaction?.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZoneName: 'short'
                    })}
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 ${getStatusColor(transaction?.status)} shadow-lg`}>
                    {getStatusIcon(transaction?.transaction_status?.status)}
                    {(transaction?.transaction_status?.status || 'Completed').toUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Transaction Summary */}
          <div className="p-6 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              TRANSACTION SUMMARY
            </h3>
            
            {/* Financial Summary Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                {/* Send Amount */}
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">Send Amount:</span>
                  <span className="text-lg font-bold text-slate-800">
                    {transaction?.source_currency} {parseFloat(transaction?.send_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                
                {/* Exchange Rate */}
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">Exchange Rate:</span>
                  <span className="text-lg font-bold text-slate-800">
                    1 {transaction?.source_currency} = {parseFloat(transaction?.rate || 0).toFixed(4)} {transaction?.destination_currency}
                  </span>
                </div>
                
                {/* Transaction Fee */}
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">Transaction Fee:</span>
                  <span className="text-lg font-bold text-slate-800">
                    {transaction?.source_currency} {parseFloat(transaction?.charge || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              
              {/* Recipient Amount - Highlighted */}
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl p-6 text-center shadow-lg">
                <p className="text-yellow-900 font-bold text-sm uppercase tracking-wide mb-2">Recipient Receives</p>
                <p className="text-3xl font-bold text-yellow-900">
                  {transaction?.destination_currency} {parseFloat(transaction?.receive_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <div className="mt-3 pt-3 border-t border-yellow-600">
                  <p className="text-yellow-800 text-sm font-medium">
                    Total Amount Paid: {transaction?.source_currency} {parseFloat(transaction?.send_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Sender Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-full shadow-sm flex items-center justify-center">
                      <User size={16} className="text-white flex-shrink-0" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Sender Information</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Name:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.sender?.firstname} {transaction?.sender?.lastname}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Phone:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.sender?.msisdn || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Address:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2 break-words">
                        {transaction?.sender?.address || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recipient Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-500 rounded-full shadow-sm flex items-center justify-center">
                      <User size={16} className="text-white flex-shrink-0" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Recipient Information</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Name:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.receiver?.firstname} {transaction?.receiver?.lastname}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Phone:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.receiver?.contact_number || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Address:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2 break-words">
                        {transaction?.receiver?.address || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-500 rounded-full shadow-sm flex items-center justify-center">
                      <CreditCard size={16} className="text-white flex-shrink-0" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Payment Method</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Institution:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.receiver_wallet?.destination_institution?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Account:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2 font-mono">
                        {transaction?.receiver_wallet?.account_number || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Type:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.receiver_wallet?.destination_institution?.type || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Transaction Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-indigo-500 rounded-full shadow-sm flex items-center justify-center">
                      <FileText size={16} className="text-white flex-shrink-0" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Transaction Details</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Reference:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2 font-mono break-all">
                        {transaction?.reference|| 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Partner Ref:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2 font-mono break-all">
                        {transaction?.partner_reference || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Purpose:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.transaction_purpose?.purpose || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Method:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.transaction_type?.type || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Partner:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.partner?.name || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exchange Rate */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-yellow-500 rounded-full shadow-sm flex items-center justify-center">
                      <span className="text-white font-bold text-base leading-none">₹</span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Exchange Rate</h3>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                    <div className="text-center">
                      <p className="text-sm font-medium text-yellow-800 mb-2">
                        1 {transaction?.source_currency} =
                      </p>
                      <p className="text-xl font-bold text-yellow-900">
                        {parseFloat(transaction?.rate || 0).toFixed(4)} {transaction?.destination_currency}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Agent Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-full shadow-sm flex items-center justify-center">
                      <User size={16} className="text-white flex-shrink-0" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Agent Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Agent:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.agent?.firstname} {transaction?.agent?.lastname}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Agency:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.agent?.agency?.name}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <span className="text-sm font-medium text-gray-600 col-span-1">Contact:</span>
                      <span className="text-sm font-semibold text-gray-900 col-span-2">
                        {transaction?.agent?.agency?.msisdn}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Total Amount Section */}
            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg p-6 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <span className="text-lg font-bold text-yellow-900 uppercase tracking-wide">Total Amount Paid:</span>
                  <span className="text-3xl font-bold text-yellow-900">
                    {transaction?.source_currency} {parseFloat(transaction?.send_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Footer */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-6 text-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-bold text-lg mb-2">INSTAANT MONEY TRANSFER SERVICES</h4>
                <p className="text-slate-300 text-sm mb-1">Licensed Money Transfer Operator</p>
                <p className="text-slate-300 text-sm mb-1">Regulated Financial Service Provider</p>
                <p className="text-slate-300 text-sm">Email: support@instaant.com</p>
              </div>
              <div className="text-left md:text-right">
                <p className="font-bold text-yellow-400 text-lg mb-2">OFFICIAL RECEIPT</p>
                <p className="text-slate-300 text-sm mb-1">This receipt serves as proof of transaction</p>
                <p className="text-slate-300 text-sm mb-1">Valid for regulatory compliance purposes</p>
                <p className="text-slate-300 text-sm">
                  Generated: {new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionReceipt;